/**
 * CollapsiblePhaseSection (iter84.57.26 — extracted in iter84.59.18)
 *
 * Phase wrapper with smart open/close. Operator feedback from production:
 * the Release Ops page is a tall stack of 4 phase sections + cards, and
 * operators wanted phases to start collapsed and only auto-open when
 * there's something needing attention (warn/blocker), or when the phase
 * is the currently-active one (first non-complete phase). When the last
 * gate in a phase flips green, that phase collapses and the next one
 * auto-opens, mirroring the natural runbook flow.
 *
 * Behaviour
 * ---------
 *   * Default: collapsed.
 *   * Auto-open when this phase's gates contain any `waived` (warn) or
 *     `denied` (blocker) row, OR when this is the active phase (first
 *     phase with at least one `pending` gate).
 *   * Manual click on the header overrides for the current session.
 *     Once the user manually toggles, that decision sticks until the
 *     gates' state changes again (we drop the manual override when gate
 *     states materially change, so completing a section still
 *     auto-opens the next).
 *   * Phase 0 (initiation) and Phase 4 (audit) are non-gated, so they
 *     follow simpler rules: Phase 0 is always open (it's the entry
 *     point); Phase 4 is closed by default but click-to-open.
 *
 * Extracted from ReleaseOpsTab.jsx in iter84.59.18 (tech-debt 3a sub-slice
 * — extracting self-contained inline helpers; the per-phase JSX split is
 * a follow-up iter once the foundational helpers live in their own files).
 */
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * Helper to read a per-item check off a promotion record. Exported so
 * callers can compose their own gate logic (Phase 1/3 InlineGateCard
 * `state` prop, CommitGateCard lock predicate, etc.).
 */
export function checkOf(promotion, section, itemId) {
    if (!promotion?.checks) return undefined;
    return promotion.checks[`${section}:${itemId}`];
}

/**
 * Compute the rolled-up status of a phase given its gate IDs and the
 * active promotion record. Output vocabulary:
 *   inactive (no gates) · blocker (any denied) · warn (any waived) ·
 *   complete (all complete) · pending (otherwise).
 */
export function phaseStatus(promotion, gateIds) {
    // gateIds: Array<[section, itemId]>
    if (!gateIds || gateIds.length === 0) return "inactive";
    const states = gateIds.map(([s, i]) => {
        const c = checkOf(promotion, s, i);
        return c?.state || "pending";
    });
    if (states.some((s) => s === "denied")) return "blocker";
    if (states.some((s) => s === "waived")) return "warn";
    if (states.every((s) => s === "complete")) return "complete";
    return "pending";
}

export function CollapsiblePhaseSection({
    number,
    title,
    hint,
    accent = "cyber",
    gateIds,
    promotion,
    isActivePhase,
    forceOpen = false,
    children,
}) {
    const status = phaseStatus(promotion, gateIds);
    // Auto-open if active OR has warn/blocker OR forceOpen (Phase 0).
    const autoOpen = forceOpen || isActivePhase || status === "warn" || status === "blocker";
    // Manual override: lets operator open/close any phase regardless of
    // auto-rule. The override resets whenever the underlying auto-rule
    // changes, so finishing a phase auto-collapses it on the next render
    // even if the operator had manually opened it earlier.
    const [manualOverride, setManualOverride] = useState(null); // null | true | false
    const prevAutoOpenRef = useRef(autoOpen);
    useEffect(() => {
        if (prevAutoOpenRef.current !== autoOpen) {
            setManualOverride(null);
            prevAutoOpenRef.current = autoOpen;
        }
    }, [autoOpen]);

    const open = manualOverride !== null ? manualOverride : autoOpen;

    const accentCls = {
        cyber: "text-cyber border-cyber/40",
        volt: "text-volt border-volt/40",
        emerald: "text-emerald-400 border-emerald-500/40",
        zinc: "text-zinc-400 border-zinc-700",
        fuchsia: "text-fuchsia-400 border-fuchsia-500/40",
        amber: "text-amber-400 border-amber-500/40",
    }[accent] || "text-cyber border-cyber/40";

    // Status pill — quickly conveys "this phase is X" so collapsed phases
    // still surface their state.
    const statusPill = (() => {
        if (status === "blocker") {
            return <span className="text-[10px] uppercase tracking-widest text-red-400 bg-red-500/10 border border-red-500/40 px-2 py-[1px] rounded">Blocker</span>;
        }
        if (status === "warn") {
            return <span className="text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/40 px-2 py-[1px] rounded">Warn</span>;
        }
        if (status === "complete") {
            return <span className="text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/40 px-2 py-[1px] rounded">Complete</span>;
        }
        if (status === "pending") {
            return <span className="text-[10px] uppercase tracking-widest text-zinc-400 bg-zinc-700/20 border border-zinc-600/40 px-2 py-[1px] rounded">Pending</span>;
        }
        return null;
    })();

    return (
        <div data-testid={`release-ops-phase-${number}-section`} className="mt-6">
            <button
                type="button"
                onClick={() => setManualOverride(!open)}
                data-testid={`release-ops-phase-${number}-toggle`}
                className={`w-full flex flex-col gap-1 mb-2 pb-2 border-b ${accentCls} text-left hover:bg-white/5 transition-colors px-1 -mx-1`}
                aria-expanded={open}
                aria-controls={`release-ops-phase-${number}-body`}
            >
                <div className="flex items-baseline gap-3 w-full">
                    {open ? (
                        <ChevronDown size={14} className={`shrink-0 ${accentCls.split(" ")[0]}`} />
                    ) : (
                        <ChevronRight size={14} className={`shrink-0 ${accentCls.split(" ")[0]}`} />
                    )}
                    <span className={`font-display font-extrabold text-2xl ${accentCls.split(" ")[0]}`}>
                        {number}
                    </span>
                    <h2 className="font-display font-extrabold text-sm uppercase tracking-widest text-white flex-1">
                        {title}
                    </h2>
                    {statusPill}
                </div>
                {/* iter87.110 — Hint moved to its own row under the title
                    so it flows naturally at any width. Previously it sat
                    in the same flex row with a narrow `flex-1` column,
                    which crushed to one-word-per-line on mobile. Indent
                    matches the title (past the chevron + number). */}
                {hint && (
                    <span
                        className="text-[10px] text-zinc-500 italic leading-relaxed pl-[46px]"
                        data-testid={`release-ops-phase-${number}-hint`}
                    >
                        {hint}
                    </span>
                )}
            </button>
            {open && (
                <div id={`release-ops-phase-${number}-body`} data-testid={`release-ops-phase-${number}-body`}>
                    {children}
                </div>
            )}
        </div>
    );
}
